import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import DOMPurify from "dompurify";
import useScrollToTop from "../hooks/useScrollToTop";
import { getPrestations } from "../services/prestationsServices";
import { LanguageContext } from "../contexts/LanguageContext";
import { FaTimes } from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch {
    return false;
  }
};

const absUrl = (u) => (u ? (isExternalURL(u) ? u : `${BASE_URL}${u}`) : "");

const PrestationsPage = () => {
  useScrollToTop();

  const { language } = useContext(LanguageContext);

  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);

  // modal image
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const getLocalizedKey = useCallback(
    (key) => `${key}_${String(language || "FR").toLowerCase()}`,
    [language]
  );

  useEffect(() => {
    const fetchPrestations = async () => {
      setLoading(true);
      try {
        const response = await getPrestations();
        const sorted = (response || []).slice().sort((a, b) => {
          const ao = Number(a?.prestation_ordre ?? 0);
          const bo = Number(b?.prestation_ordre ?? 0);
          return ao - bo;
        });
        setPrestations(sorted);
      } catch (error) {
        console.error("Erreur lors de la récupération des prestations :", error);
        setPrestations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPrestations();
  }, []);

  const openModal = useCallback((image) => {
    const u = absUrl(image);
    if (!u) return;
    setSelectedImage(u);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedImage(null);
  }, []);

  // ESC + scroll lock modal
  useEffect(() => {
    if (!isModalOpen) return;

    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isModalOpen, closeModal]);

  // UI helpers
  const sectionWrap = "relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8";
  const card =
    "rounded-3xl overflow-hidden border border-black/10 dark:border-white/10 " +
    "bg-[#E8BB89]/80 dark:bg-white/5 shadow-sm backdrop-blur-sm";
  const softText = "text-gray-900 dark:text-white";
  const mutedText = "text-gray-800 dark:text-gray-300";
  const buttonPrimary =
    "inline-flex items-center justify-center rounded-2xl bg-primary px-6 py-3 text-white shadow " +
    "hover:opacity-95 transition active:scale-[0.99]";

  const heroTitle = useMemo(
    () => (language === "FR" ? "Prestations" : "Services"),
    [language]
  );

  const heroSubtitle = useMemo(
    () =>
      language === "FR"
        ? "Découvrez les prestations proposées, pensées pour votre équilibre et votre bien-être."
        : "Discover the services offered, designed for balance and well-being.",
    [language]
  );

  if (loading) {
    return (
      <p className="text-center text-gray-600 dark:text-gray-300 py-12">
        {language === "FR" ? "Chargement des prestations..." : "Loading services..."}
      </p>
    );
  }

  return (
    <div className="relative min-h-screen pb-12">
      {/* Décor */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute top-[60px] left-0 w-full h-[34vh] bg-cover bg-no-repeat bg-bottom opacity-60 dark:opacity-25"
          style={{ backgroundImage: "url('/images/fond_sakura.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/35 to-transparent dark:from-black/60" />
      </div>

      {/* HERO */}
      <section className={`${sectionWrap} pt-24 sm:pt-28`}>
        <div className="text-center">
          <div
            className="
              inline-flex items-center gap-2 rounded-full px-4 py-2
              bg-black/5 dark:bg-white/10
              border border-black/10 dark:border-white/10
              backdrop-blur-sm
            "
          >
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs sm:text-sm font-satoshi font-semibold tracking-wide text-gray-700 dark:text-gray-200">
              {language === "FR" ? "Zen Shiatsu" : "Zen Shiatsu"}
            </span>
          </div>

          <h1 className={`mt-4 text-3xl sm:text-4xl lg:text-5xl font-satoshi font-extrabold tracking-tight ${softText}`}>
            {heroTitle}
          </h1>

          <div className="mt-4 flex justify-center">
            <div className="h-[3px] w-24 sm:w-32 rounded-full bg-primary/90" />
          </div>

          <p className={`mt-4 max-w-3xl mx-auto text-sm sm:text-base ${mutedText}`}>
            {heroSubtitle}
          </p>

          <div className="mt-6 flex justify-center">
            <a href="/prise_rdv" className={buttonPrimary}>
              {language === "FR" ? "Prendre rendez-vous" : "Make an appointment"}
            </a>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className={`${sectionWrap} mt-10`}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
          {prestations.map((p) => {
            const title =
              p?.[getLocalizedKey("prestation_title")] || (language === "FR" ? "Titre indisponible" : "Title unavailable");

            const content =
              p?.[getLocalizedKey("prestation_content")] || (language === "FR" ? "<p>Contenu indisponible</p>" : "<p>Content unavailable</p>");

            const img = absUrl(p?.prestation_image);

            return (
              <article
                key={p.id}
                className={`
                  ${card}
                  transition-transform duration-300
                  hover:-translate-y-1
                `}
              >
                {/* Image (cadre strict, pas de débordement) */}
                <button
                  type="button"
                  onClick={() => openModal(p?.prestation_image)}
                  className="
                    group relative w-full text-left
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                  "
                  aria-label={language === "FR" ? "Agrandir l'image" : "Open image"}
                >
                  <div className="relative w-full aspect-square bg-black/5 dark:bg-white/5">
                    {img ? (
                      <img
                        src={img}
                        alt={title}
                        className="
                          h-full w-full object-contain
                          transition-opacity duration-300
                          group-hover:opacity-95
                        "
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-full w-full" />
                    )}

                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent opacity-70" />

                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-3">
                      <span className="text-xs text-white/90 bg-black/30 px-2 py-1 rounded-full border border-white/10 backdrop-blur">
                        {language === "FR" ? "Aperçu" : "Preview"}
                      </span>

                      <span
                        className="
                          inline-flex items-center justify-center rounded-2xl px-3 py-2
                          bg-white/15 backdrop-blur-md border border-white/15
                          text-white text-xs font-semibold
                          transition group-hover:bg-white/20
                        "
                      >
                        {language === "FR" ? "Agrandir" : "Open"}
                      </span>
                    </div>
                  </div>
                </button>

                {/* Texte */}
                <div className="p-5 sm:p-6">
                  <h2 className={`text-lg sm:text-xl font-satoshi font-extrabold tracking-tight ${softText}`}>
                    {title}
                  </h2>

                  <div
                    className={`
                      mt-3 prose prose-neutral max-w-none
                      dark:prose-invert
                      prose-p:leading-relaxed
                      ${mutedText}
                    `}
                    style={{ wordWrap: "break-word" }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(content),
                    }}
                  />

                  <div className="mt-5">
                    <a href="/prise_rdv" className="inline-flex text-primary font-semibold hover:underline">
                      {language === "FR" ? "Prendre rendez-vous →" : "Book an appointment →"}
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* CTA bottom */}
        <div className="mt-10 flex justify-center">
          <a href="/prise_rdv" className={buttonPrimary}>
            {language === "FR" ? "Prendre rendez-vous" : "Make an appointment"}
          </a>
        </div>
      </section>

      {/* MODAL IMAGE */}
      {isModalOpen && selectedImage && (
        <div
          className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="
              relative w-full max-w-5xl
              rounded-3xl overflow-hidden
              border border-white/10
              bg-black/35 shadow-2xl
            "
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 sm:p-5">
              <div className="text-white">
                <div className="font-semibold">{language === "FR" ? "Agrandissement" : "Preview"}</div>
                <div className="text-xs text-white/70">{language === "FR" ? "Échap pour fermer" : "Esc to close"}</div>
              </div>

              <button
                className="
                  w-10 h-10 rounded-2xl
                  bg-white/10 hover:bg-white/15
                  border border-white/10
                  text-white transition
                "
                onClick={closeModal}
                aria-label={language === "FR" ? "Fermer" : "Close"}
                title={language === "FR" ? "Fermer" : "Close"}
              >
                <FaTimes className="mx-auto" />
              </button>
            </div>

            <div className="px-4 pb-5 flex items-center justify-center">
              <img
                src={selectedImage}
                alt="Agrandissement"
                className="max-h-[78vh] max-w-[92vw] object-contain rounded-2xl bg-black/20"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrestationsPage;
